<template>
  <div>
    <b-container fluid>
      <leaflet-map v-if="this.vehicleTypes" :zoom="12" style="width: 100%; height: 500px" ref="fullMap" :fit-always="true" />
    </b-container>
    <b-container id="view-tour" v-if="tour">
      <b-row>
        <b-col>
          <b-card>
            <b-form-checkbox v-model="showTracking" v-if="isSuperAdmin || isOperatorSupervisor || isOperator || isAccountant">
              <b-spinner small v-if="trackingWorking"></b-spinner>
              <translate>Show courier tracking</translate>
            </b-form-checkbox>
            <b-form-checkbox v-if="showTracking" v-model="showHeathPath" :disabled="trackingWorking">
              <translate>Show courier heathPath</translate>
            </b-form-checkbox>

            <b-alert :variant="tour.warehouse_slot.content.length === tour.shippings.length ? 'success' : 'warning'" show v-if="tour.warehouse_slot" class="mt-3">
              <h2 class="mb-0">
                <span v-translate="{reference: tour.warehouse_slot.reference, total: tour.shippings.length, scanned: tour.warehouse_slot.content.length}">Slot: %{reference} (%{scanned}/%{total})</span>
                <b-button size="sm" variant="danger" @click="releaseSlot" v-if="isSuperAdmin || isOperatorSupervisor || isAdmin" v-translate>
                  Force Release Slot
                </b-button>
              </h2>
            </b-alert>

            <h3>
              <span v-translate="{id: tour.id}">Tour #%{id}</span>
              <span :title="!notOptimized ? $gettext('Optimized Tour :)') : $gettext('This tour has been modified and it IS NOT optimized')" style="display: inline-block; width:30px; height:30px; border-radius: 100px;" :class="tour.status === 'DRAFT' ? 'bg-danger' : (!notOptimized ? 'bg-success' : 'bg-warning')"></span>
            </h3>
            <h4 v-if="tour.label" v-translate="{data: tour.label}">Label: <strong>%{data}</strong></h4>
            <p v-if="tour.applied_by_id">
              <translate>Applied by:</translate>
              <a :href="'/edit/users/'+ tour.applied_by.id" target="_blank">#{{tour.applied_by.id}}</a> <strong>{{tour.applied_by.name}}</strong></p>
            <b-button size="sm" variant="dark" class="float-right mb-1" v-b-modal.modalModifyTourCommission v-show="(isSuperAdmin || isAdmin || isHeadOperations) && !tour.courier_id" style="padding: 0.1rem 1rem;" v-translate>
              Modify Tour Commission
            </b-button>

            <b-modal id="modalModifyTourCommission" ref="modalModifyTourCommission" hide-footer @hide="tourCommissionPercentage = null">
              <div slot="modal-title" v-translate>Modify Tour Commission</div>
              <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
              <form @submit.stop.prevent="modifyTourCommission()">
                <label for="tour-commission-percentage" v-translate>Tour commission percentage</label>
                <b-input-group>
                  <b-input type="number" min="-100" max="100" id="tour-commission-percentage" v-model="tourCommissionPercentage" />
                  <b-input-group-append>
                    <b-input-group-text>%</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
                <b-form-group class="form-actions">
                  <b-button variant="primary" type="submit" :disabled="!tourCommissionPercentage" v-translate>
                    Modify Commission
                  </b-button>
                  <b-button variant="secondary" @click.prevent="$refs.modalModifyTourCommission.hide()" v-translate>
                    Cancel
                  </b-button>
                </b-form-group>
              </form>
            </b-modal>

            <b-button size="sm" variant="warning" class="mb-1" @click.prevent="recalculateBudget" v-show="(isSuperAdmin || isAdmin || isOperator || isOperatorSupervisor) && tour.status !== 'FINISHED' && tour.status !== 'CANCELED'" style="padding: 0.1rem 1rem;" v-translate>
              Recalculate Budget
            </b-button>
            <hr>
            <b-alert variant="warning" :show="tour.status !== 'DRAFT' && notOptimized && !tour.reprocess">
              <h4 v-translate>This tour has been modified and it IS NOT optimized</h4>
              <b-button variant="warning" @click="optimizeTour" v-translate>Resend to Routifier!</b-button>
            </b-alert>
            <b-alert variant="warning" :show="tour.reprocess === 1">
              <h4 v-translate>This tour has been modified and it's currently being re-routed</h4>
              <p v-translate>Please, wait a minute and reload the page until it's finished.</p>
            </b-alert>
            <b-row>
              <b-col md="7">
                <h2>
                  <span v-translate="{data: tour.status}">Status: <strong>%{data}</strong></span>
                  <small v-if="tour.has_relevant_centroid === false">
                    <b-badge variant="info" v-translate>Street</b-badge>
                  </small>
                  <small v-if="tour.fixed_amount_price_schedules && tour.fixed_amount_price_schedules.length > 0 && tour.fixed_amount_price_schedules[0] && tour.fixed_amount_price_schedules[0].status !== 'canceled'">
                    <b-badge variant="success" :id="'fixed-price-' + tour.fixed_amount_price_schedules[0].id" v-translate>FIXED PRICE</b-badge>
                  </small>
                  <b-tooltip v-if="tour.fixed_amount_price_schedules && tour.fixed_amount_price_schedules.length > 0 && tour.fixed_amount_price_schedules[0] && tour.fixed_amount_price_schedules[0].status !== 'canceled'" :target="'fixed-price-' + tour.fixed_amount_price_schedules[0].id">
                    <div class="m-2">
                      <span v-translate="{data: moment.utc(tour.fixed_amount_price_schedules[0].from_date).tz($tz).format('L LT')}"><abbr title="Estimated">Est.</abbr> Start: %{data}</span><br>
                      <span v-translate="{data: moment.utc(tour.fixed_amount_price_schedules[0].to_date).tz($tz).format('L LT')}"><abbr title="Estimated">Est.</abbr> End: %{data}</span><br>
                      <span v-translate="{data: tour.fixed_amount_price_schedules[0].amount + ' ' + tour.fixed_amount_price_schedules[0].currency}">Amount: %{data}</span><br>
                      <span v-translate="{maxExtraHours: tour.fixed_amount_price_schedules[0].fixed_tour.max_extra_hours, price: tour.fixed_amount_price_schedules[0].fixed_tour.extra_hour_price}"><abbr title="Maximum Extra Hours">M.E.H.</abbr>: %{maxExtraHours} <small>(%{price}/Hour)</small></span>
                    </div>
                  </b-tooltip>
                </h2>
                <div class="mb-3">
                  <div class="text-nowrap" v-translate="{distance: tour.total_distance || '-'}">
                    Tour distance: <strong>%{distance} km.</strong>
                  </div>
                  <div class="text-nowrap" v-translate="{etc: moment().startOf('day').add(tour.estimated_completion_time, 'minutes').format(tour.estimated_completion_time < 60 ? 'm' : 'hh:mm') + (tour.estimated_completion_time < 60 ? ' min' : ' hs')}">
                    ETC: <strong>%{etc}</strong>
                  </div>
                  <div class="text-nowrap" v-translate="{lastUpdated: moment.utc(tour.updated_at).tz($tz).format('L LT')}">
                    Last updated: <strong>%{lastUpdated}</strong>
                  </div>
                </div>
              </b-col>
              <b-col class="text-right">
                <b-button variant="secondary" size="lg" v-b-modal.modalVisitsOverview :disabled="tour.status === 'DRAFT'" v-translate>Visits Overview</b-button>
              </b-col>
            </b-row>
            <div v-if="tour.shippings && tour.shippings.length > 0">
              <hr class="mt-0">
              <b-row>
                <b-col v-if="tour.courier_id !== null">
                  <h4>
                    <translate>Current Courier</translate>
                    <b-button variant="danger" size="sm" @click="unassignTour" v-show="noVisitsYet && tour.status !== 'FINISHED' && tour.status !== 'CANCELED'" v-translate>UNASSIGN Tour</b-button>
                    <b-button variant="warning" size="sm" @click="reassigning = true" v-b-modal.modalAssignNormal v-show="(isSuperAdmin || isAdmin || isOperatorSupervisor || isHeadOperations || isOperator) && !noVisitsYet && tour.status !== 'FINISHED' && tour.status !== 'CANCELED'" v-translate>REASSIGN Tour</b-button>
                  </h4>
                  <user-card :user='tour.courier' profile-picture-size="90px" />
                </b-col>
                <b-col>
                  <b-row v-if="tour.fixed_amount_price_schedules[0] && tour.fixed_amount_price_schedules[0].status !== 'canceled'">
                    <b-col>
                      <h5 v-translate>Defined Fixed Price</h5>
                      <span v-translate="{data: moment.utc(tour.fixed_amount_price_schedules[0].from_date).tz($tz).format('L LT')}"><abbr title="Estimated">Est.</abbr> Start: %{data}</span><br>
                      <span v-translate="{data: moment.utc(tour.fixed_amount_price_schedules[0].to_date).tz($tz).format('L LT')}"><abbr title="Estimated">Est.</abbr> End: %{data}</span><br>
                      <span v-translate="{data: tour.fixed_amount_price_schedules[0].amount + ' ' + tour.fixed_amount_price_schedules[0].currency}">Amount: %{data}</span><br>
                      <span v-translate="{maxExtraHours: tour.fixed_amount_price_schedules[0].fixed_tour.max_extra_hours, price: tour.fixed_amount_price_schedules[0].fixed_tour.extra_hour_price}"><abbr title="Maximum Extra Hours">M.E.H.</abbr>: %{maxExtraHours} <small>(%{price}/Hour)</small></span>
                      <div v-html="sanityDetail(tour)"></div>
                    </b-col>
                    <b-col v-if="tour.fixed_amount_price_schedules[0] && tour.fixed_amount_price_schedules[0].status !== 'canceled' && tour.finished_at">
                      <h5 v-translate>Processed Fixed Price</h5>
                      <span v-translate="{data: moment.utc(tour.started_at).tz($tz).format('L LT')}">Started at: %{data}</span><br>
                      <span v-translate="{data: moment.utc(tour.finished_at).tz($tz).format('L LT')}">Ended at: %{data}</span><br>
                      <span v-translate="{data: courierProfit + ' ' + tour.fixed_amount_price_schedules[0].currency}">Courier profit: %{data}</span><br>
                      <span v-translate="{data: clientCost + ' ' + tour.fixed_amount_price_schedules[0].currency}">Client cost: %{data}</span><br>
                      <span :class="(clientCost - courierProfit) > 0 ? 'text-success' : 'text-danger'">
                        <span v-translate="{data: ourProfit + ' ' + tour.fixed_amount_price_schedules[0].currency}">Our profit: %{data}</span>
                        <span v-if="(clientCost - courierProfit) > 0">😃</span><span v-else>😞</span>
                      </span>
                    </b-col>
                  </b-row>
                  <b-row v-else>
                    <b-col>
                      <h5 v-translate>Sanity Check</h5>
                      <div v-html="sanityDetail(tour)"></div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
        <b-col cols="3" class="text-right" v-if="tour.status !== 'FINISHED' && tour.status !== 'CANCELED'">
          <br>
          <div v-if="tour.status !== 'DRAFT'">
            <b-button block variant="info" @click="optimizeTour" v-translate>Force resend to Routifier!</b-button>
            <b-button block variant="primary" v-b-modal.modalAssignSlot :disabled="tour.status !== 'READY'" v-show="warehouse && warehouse.id" v-translate>Assign/update WH Slot</b-button>
            <b-button block variant="dark" @click="takeCheck('waiting')" :disabled="(tour.status !== 'READY' && tour.status !== 'WAITING') || tour.courier_id !== null" v-show="!waiting" v-translate>Set Tour as WAITING</b-button>
            <b-button block variant="dark" @click="setTourAsReady" :disabled="(tour.status !== 'READY' && tour.status !== 'WAITING') || tour.courier_id !== null" v-show="waiting" v-translate>Reset Tour as READY</b-button>
            <div>
              <br>
              <b-button block @click="takeCheck('assign')" variant="secondary" :disabled="(tour.status !== 'READY' && tour.status !== 'WAITING') || tour.courier_id !== null" v-translate>
                Assign Courier Normally
              </b-button>
              <b-button block v-b-modal.modalFixedPrice variant="dark" :disabled="(tour.status !== 'READY' && tour.status !== 'WAITING') || tour.courier_id !== null" v-if="isSuperAdmin || isAdmin || isHeadOperations" v-translate>
                Assign With Fixed Price
              </b-button>

              <b-modal id="modalAssignSlot" ref="modalAssignSlot" hide-footer @show="fetchFreeSlots()" @hide="freeSlots = null; electedSlot = null">
                <div slot="modal-title" v-translate>Assign/update WH Slot</div>
                <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
                <div v-if="freeSlots && freeSlots.length > 0">
                  <div class="form-group">
                    <label for="search-courier" v-translate>Select the WH Slot</label>
                    <b-form-select v-model="selectedSlot" :options="freeSlots.map(x => { return {text: x.reference, value: x.id} })" class="mb-3">
                      <template #first>
                        <option :value="null" v-translate>Select Free Slot</option>
                      </template>
                    </b-form-select>
                  </div>
                  <b-col cols="12" class="text-center">
                    <b-button @click="assignSlot" :disabled="!selectedSlot" v-translate>Assign/update!</b-button>
                  </b-col>
                </div>
                <div class="my-4" v-else-if="!freeSlots">
                  <h4>
                    <b-spinner label="Loading..." />
                    <translate>Looking for free Slots</translate>
                  </h4>
                </div>
                <div class="my-5" v-else>
                  <h4 v-translate>There are no free Slots in the Warehouse</h4>
                </div>
              </b-modal>

              <b-modal id="modalAssignNormal" ref="modalAssignNormal" hide-footer @hide="newCourier = null; reassigning = false;">
                <div slot="modal-title">
                  <translate v-if="!reassigning">Assign Courier Normally</translate>
                  <translate v-else>Reassign Courier</translate>
                </div>
                <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
                <div class="form-group">
                  <label for="search-courier" v-translate>Search for courier by name or email</label>
                  <user-autocomplete ref="courierAc" :placeholder="$gettext('Courier Name or Email')" @selected="onSearchCourierSelected" :restrictToRoles="['courier']" with-picture />
                </div>
                <b-row v-if="newCourier">
                  <user-card :user='newCourier' profile-picture-size="90px" />
                  <b-col cols="12" class="text-right">
                    <b-button @click="assignCourier" v-if="!reassigning" v-translate>Assign!</b-button>
                    <b-button @click="reassignCourier" :disabled="working" v-else v-translate>Reassign!</b-button>
                  </b-col>
                </b-row>
              </b-modal>

              <b-modal id="modalFixedPrice" ref="modalFixedPrice" hide-footer>
                <div slot="modal-title" v-translate>Assign With Fixed Price</div>
                <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
                <fixed-price-form :tour-id="tour.id" @success="fixedPriceSuccess" @cancel="fixedPriceCancel" />
              </b-modal>
            </div>
            <br>
            <br>
          </div>
          <b-button block variant="danger" @click="cancelTour" v-if="tour.status !== 'CANCELED'" v-translate>CANCEL Tour</b-button>
        </b-col>
      </b-row>
    </b-container>
    <b-container>
      <b-row>
        <b-col cols="12">

          <comments-box-single-field comment-field-name="comment" :payload-field="this.tour" :endpoint="this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id" @success="getTour" @delete="getTour" :add-condition="(isSuperAdmin || isAdmin || isOperator || isOperatorSupervisor) && tour.status !== 'FINISHED' && tour.status !== 'CANCELED'" />

        </b-col>
      </b-row>
    </b-container>

    <b-container id="view-tour">
      <h3 v-translate>Manage Tour Shippings</h3>
      <b-card v-if="tour.status !== 'FINISHED' && tour.status !== 'CANCELED'">
        <form @submit.prevent="addShippingToTour">
          <b-row>
            <b-col lg="4">
              <label for="add-shipping" v-translate>Shipping IDs (one per line)</label>
              <b-form-textarea rows="3" max-rows="6" id="add-shipping" v-model="newShippings" required></b-form-textarea>
            </b-col>
            <b-col lg="8"><br>
              <b-button variant="secondary" size="sm" type="submit" class="shipping-list-item-cancel align-self-center" v-translate>Add shipping/s to this Tour</b-button>
            </b-col>
          </b-row>
        </form>
      </b-card>

      <b-card v-for="(shipping, index) in shippings" :key="index" class="shipping-item" :class="shipping.status.toLowerCase()">
        <span style="position:absolute; top: 0; right: 0; padding: 0 10px" class="bg-success text-light" v-if="tour.warehouse_slot && tour.warehouse_slot.content.map(x => x.shipping_id).indexOf(shipping.id) !== -1">
          <small><translate>In Slot</translate></small>
        </span>
        <b-row>
          <b-col cols="12" md="6" lg="3" class="shipping-status mb-3 mb-lg-0" order="1">
            <div class="spinner" v-bind:class="{ loading: shipping.status === 'WAITING' && !isScheduled }">
              <div class="loader">
                <div class="loader">
                  <div class="loader">
                    <div class="loader">
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="spinner clock" v-bind:class="{ loading: shipping.status === 'WAITING' && isScheduled }"><font-awesome-icon :icon="['far', 'clock']" size="1x" /></div>
            <div md="12" v-show="shipping.status === 'WAITING' && !isScheduled" class="status" v-translate>
              Searching for Moover
            </div>
            <div md="12" v-show="shipping.status === 'WAITING' && isScheduled" class="status" v-translate="{scheduledDate: moment.utc(shipping.scheduled_date).tz($tz).format('L LT')}">Scheduled for %{scheduledDate}</div>
            <div md="12" v-show="shipping.status === 'READY'" class="status" v-translate translate-context="statuses">
              Ready to be scheduled
            </div>
            <div md="12" v-show="shipping.status === 'CONFIRMED'" class="status" v-translate>
              Moover in transit to origin
            </div>
            <div md="12" v-show="shipping.status === 'ATPICKUPPOINT'" class="status" v-translate>
              Moover arrived to origin
            </div>
            <div md="12" v-show="shipping.status === 'INTRANSIT'" class="status" v-translate>
              Moover in transit to destination
            </div>
            <div md="12" v-show="shipping.status === 'DELIVERED'" class="status" v-translate>
              Shipping delivered! :)
            </div>
            <div md="12" v-show="shipping.status === 'CANCELED'" class="status" v-translate>
              Shipping canceled! :(
            </div>
            <div>
              <strong class="shipping-id"><a :href="'/edit/shippings/' + shipping.id" target="_blank">#{{shipping.id | uuid}}</a> ({{shippingSizes.find(x => x.id === shipping.size_id).name}})</strong><br>
              <span class="shipping-date">{{moment.utc(shipping.created_at).tz($tz).format('L LT')}}</span>
            </div>
            <div v-if="shipping.company_id">
              <translate>Company:</translate>
              <a :href="'/edit/companies/'+ shipping.company_id" target="_blank">#{{shipping.company_id}}</a> {{shipping.company.name}}
            </div>

          </b-col>
          <b-col cols="12" md="6" lg="3" class="shipping-origin mb-3 mb-lg-0" order="3" order-lg="2">
            <h5 class="shipping-contact">{{shipping.from_contact}}</h5>
            <p><span class="shipping-address">{{shipping.last_known_place.formatted_address}}</span>
              <span v-if="(shipping.status !== 'WAITING' && shipping.status !== 'CANCELED') && !isScheduled">
                <br v-if="shipping.from_email || shipping.from_phone">
                {{shipping.from_email}}<br v-if="shipping.from_email">
                {{shipping.from_phone}}
              </span>
            </p>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="shipping-destination mb-3 mb-lg-0" order="4" order-lg="3">
            <h5 class="shipping-contact">{{shipping.to_contact}}</h5>
            <p><span class="shipping-address">{{shipping.address_to.formatted_address}}</span>
              <span v-if="(shipping.status !== 'WAITING' && shipping.status !== 'CANCELED') && !isScheduled">
                <br v-if="shipping.to_email || shipping.to_phone">
                {{shipping.to_email}}<br v-if="shipping.to_email">
                {{shipping.to_phone}}
              </span>
            </p>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="shipping-actions text-right ml-n5 mb-3 mb-lg-0 align-self-center" style="padding-left: 35px;" order="2" order-lg="4">
            <b-button v-show="shipping.status !== 'PICKEDUP' && shipping.status !== 'INTRANSIT' && shipping.status !== 'DELIVERED' && shipping.status !== 'CANCELED' && shipping.status !== 'RETURNED'" variant="outline-danger" v-b-modal.modalDeleteFromTour size="sm" @click.stop.prevent="toDelete = shipping" class="shipping-list-item-cancel align-self-center" v-translate>DELETE from this Tour</b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-container>
    <b-modal id="modalVisitsOverview" size="lg" ref="modalVisitsOverview">
      <div slot="modal-title" v-translate>Visits Overview</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <bRow>
        <bCard v-for="(visit, index) in visits4Show" :key="index" :class="visit.visited === 1 ? 'bg-success text-light' : ''">
          <bRow>
            <bCol cols="1">
              <h1>{{index+1}}</h1>
            </bCol>
            <bCol cols="11">
              <p v-translate="{data: visit.shipping_ids.join(', ')}">ID/s: <strong>%{data}</strong></p>
              <p>
                <span v-translate="{data: visit.type + (visit.shipping_ids.length > 1 ? ' multiple' : '')}">Type: <strong>%{data}</strong></span><br>
                <span v-translate="{data: visit.address_name}">Address: <strong>%{data}</strong></span><br>
                <span v-translate="{data: visit.address_extra}">Address Extra: <strong>%{data}</strong></span>
                <br>
                <span v-translate="{data: visit.visited === 1 ? $gettext('YES') : $gettext('NO')}">Visited: <strong>%{data}</strong></span>
              </p>
            </bCol>
          </bRow>
        </bCard>
      </bRow>
    </b-modal>
    <b-modal id="modalDeleteFromTour" ref="modalDeleteFromTour" :hide-footer="true">
      <div slot="modal-title" v-translate>Delete Shipping from Tour</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <form @submit.stop.prevent="deleteFromTour">
        <div class="form-group col-12">
          <b-form-radio-group v-model="deleteType" name="deleteType" required>
            <b-form-radio value="A">
              <translate>Mark as READY and DELETE (Op must manage the shipping again later)</translate>
            </b-form-radio>
            <b-form-radio value="B">
              <translate>Mark as WAITING and DELETE (the system will find and assign the shipping to the best courier option immediately)</translate>
            </b-form-radio>
          </b-form-radio-group>
        </div>
        <b-row>
          <div class="text-center form-group col-12 form-actions">
            <b-button type="submit" variant="danger" size="lg" v-translate>Delete!</b-button>
            <b-button type="button" variant="outline-primary" size="lg" @click.prevent="$refs.modalDeleteFromTour.hide()" v-translate>Cancel</b-button>
          </div>
        </b-row>
      </form>
    </b-modal>
    <div class="modal-backdrop d-flex text-center" v-if="updating">
      <div class="align-self-center" style="flex-grow: 1">
        <b-spinner variant="white" />
      </div>
    </div>
  </div>
</template>

<script>
import ToursMixin from '@/mixins/tours.mixin'
import AddressMixins from '@/mixins/address.mixin.js'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import FixedPriceForm from '@/components/FixedPriceForm'
import UserCard from '@/components/UserCard'
import LeafletMap from '@/components/LeafletMap'
import CommentsBoxSingleField from '@/components/CommentsBoxSingleField'

export default {
  components: {
    UserAutocomplete,
    FixedPriceForm,
    UserCard,
    LeafletMap,
    CommentsBoxSingleField
  },
  data() {
    return {
      tour: {},
      visits: [],
      shippings: [],
      visits4Show: [],
      markers: [],
      center: '0, 0',
      visitsOpened: false,
      searchQuery: '',
      selectedSearchItem: null,
      newCourier: null,
      acOptions: {
        debounce: 500,
        placeholder: this.$gettext('Courier Name or Email'),
        inputClass: 'form-control'
      },
      minCharacter: 2,
      notOptimized: false,
      isScheduled: null, // FAKE
      newShippings: null,
      toDelete: {},
      deleteType: null,
      start_location: {},
      addressAcInput: '',
      courierTracking: {},
      waiting: false,
      forceReroute: false,
      newComment: null,
      showTracking: false,
      heathPath: [],
      showHeathPath: false,
      trackingWorking: false,
      warehouse: null,
      freeSlots: null,
      selectedSlot: null,
      reassigning: false,
      working: false
    }
  },
  computed: {
    courierCredits() {
      return this.tour.account_transactions.filter(x => x.relationship === 'provider' && x.type === 'credit')
        .map(x => parseFloat(x.amount))
        .reduce((total, num) => total + num, 0)
    },
    courierDebits() {
      return this.tour.account_transactions.filter(x => x.relationship === 'provider' && x.type === 'debit')
        .map(x => parseFloat(x.amount))
        .reduce((total, num) => total + num, 0)
    },
    courierProfit() {
      return (Math.round(((this.courierCredits - this.courierDebits) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    clientCredits() {
      return this.tour.account_transactions.filter(x => x.relationship === 'client' && x.type === 'credit')
        .map(x => parseFloat(x.amount))
        .reduce((total, num) => total + num, 0)
    },
    clientDebits() {
      return this.tour.account_transactions.filter(x => x.relationship === 'client' && x.type === 'debit')
        .map(x => parseFloat(x.amount))
        .reduce((total, num) => total + num, 0)
    },
    clientCost() {
      return (Math.round(((this.clientDebits - this.clientCredits) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    ourProfit() {
      return (Math.round(((this.clientCost - this.courierProfit) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    noVisitsYet() {
      return this.visits.findIndex(x => x.visited === 1) === -1
    }
  },
  watch: {
    markers: function (val) {
      setTimeout(function () {
        this.$refs.fullMap.addMarkers(val)
        let polyLines = [
          {
            polyLine: val.filter(x => x.id > 10000),
            polyLineOptions: {strokeColor: '#ff6600'}
          }
        ]
        polyLines.push({
          polyLine: val.filter(x => x.id < 10000),
          polyLineOptions: {strokeColor: '#000000'}
        })
        this.$refs.fullMap.addPolylines(polyLines)
      }.bind(this, val), 100)
    },
    showTracking(val) {
      if (val) {
        this.trackingWorking = true
        const params = {
          page: 0,
          limit: 1,
          date_from: this.tour.started_at ? this.moment.utc(this.tour.started_at).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss') : this.moment.utc(this.tour.updated_at).tz(this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
          date_to: this.tour.started_at ? this.moment.utc(this.tour.started_at).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss') : this.moment.utc(this.tour.updated_at).tz(this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
          filter_groups: [
            {
              filters: [
                ['user_id', 'eq', this.tour.courier_id]
              ]
            }
          ]
        }

        this.$http.get(this.$appConfig.api.baseUrl + 'tracking-history', {params: params})
          .then(response => {
            console.log(response)
            let markers = []
            this.markers = this.markers.filter(x => x.id >= 10000)
            const uniqueTrackingPointsByGeo = response.body.data.filter((thing, index, self) =>
              index === self.findIndex((t) => {
                return thing.lat === t.lat && thing.lng === t.lng
              })
            )
            this.trackingWorking = false

            let timeThreshold = null
            uniqueTrackingPointsByGeo.forEach((item, i) => {
              if (timeThreshold === null || parseInt(item.created_at.replace(/\D/g, '')) > timeThreshold) {
                markers.push({
                  id: 1000 + i,
                  lat: item.lat,
                  lng: item.lng,
                  icon: {
                    type: 'marker-circle',
                    size: [10, 10],
                    anchor: [5, 5],
                    color: '#000',
                    text: (i + 1)
                  },
                  x: 11,
                  y: 34,
                  infoText: (i + 1) + ': ' + this.moment.utc(item.created_at).tz(this.$tz).format('L LT'),
                  tooltip: true,
                  z: uniqueTrackingPointsByGeo[(i + 1)] ? this.getSpeed(item, uniqueTrackingPointsByGeo[(i + 1)]) : 0
                })
                timeThreshold = parseInt(item.created_at.replace(/\D/g, '')) + 10
              }
            })
            this.markers = this.markers.concat(markers)
            this.heathPath = markers.map(x => {
              return [x.lat, x.lng, x.z]
            })
          })
      } else {
        this.markers = this.markers.filter(x => x.id >= 10000)
      }
    },
    showHeathPath(val) {
      if (val) {
        this.$refs.fullMap.addHeathPath(this.heathPath)
      } else {
        this.$refs.fullMap.removeHeathPath()
      }
    }
  },
  mixins: [
    AddressMixins,
    ToursMixin
  ],
  mounted() {
    this.fetchWarehouses()
    this.fetchShippingSizes()
    this.fetchVehicleTypes()
    this.getTour()
  },
  methods: {
    addCourier(courierData) {
      console.log(courierData)
      let currentPlace = {
        geometry: {
          location: {
            lat: function () {
              return parseFloat(courierData.lat)
            },
            lng: function () {
              return parseFloat(courierData.lng)
            }
          }
        }
      }
      this.$refs.fullMap.setPlace(currentPlace)
      this.$refs.fullMap.addMarker(courierData.icon, courierData.id, courierData.x, courierData.y, courierData.infoText)
    },
    async getTour() {
      const params = {
        includes: [
          'visits',
          'shippings.company',
          'shippings.to_area.regions',
          'shippings.last_known_place',
          'courier.courier_available.tracking',
          'fixed_amount_price_schedules.fixed_tour',
          'account_transactions',
          'warehouseSlot.content'
        ]
      }
      return await this.$http.get(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id, {params: params})
        .then(response => {
          this.tour = response.data
          this.visits = this.tour.visits
          this.shippings = this.tour.shippings
          this.visits4Show = []
          let prevVisit
          response.data.visits.forEach(visit => {
            this.notOptimized = this.notOptimized === true || visit.order === null || false
            if (visit.type + '-' + visit.place_id !== prevVisit) {
              visit['shipping_ids'] = [visit.shipping_id]
              // visit['visited'] = 1
              this.visits4Show.push(visit)
            } else {
              if (visit.visited === 1) {
                this.visits4Show[(this.visits4Show.length - 1)].visited = 1
              }
              this.visits4Show[(this.visits4Show.length - 1)].shipping_ids.push(visit.shipping_id)
            }
            prevVisit = visit.type + '-' + visit.place_id
          })
          if (this.tour.status === 'READY') {
            this.warehouse = this.warehouses.find(x => x.place_id === this.tour.visits[0].place_id)
          }

          if (this.tour.status === 'WAITING') {
            this.waiting = true
          } else if (this.tour.status !== 'WAITING') {
            this.waiting = false
          }

          this.tour.price = this.shippings.reduce(function (a, b) {
            return a + parseFloat(b.price)
          }, 0)
          this.tour.commission = this.tour.fixed_amount_price_schedules[0] && this.tour.fixed_amount_price_schedules[0].status !== 'canceled' ? parseFloat(this.tour.fixed_amount_price_schedules[0].amount) : this.shippings.reduce(function (a, b) {
            return a + parseFloat(b.courier_commission)
          }, 0)

          let lat
          let lng
          let icon = ''
          let markers = []
          let infoWindowHtml
          let alreadyTracking = false
          let shippingFromVisit

          for (let i = 0; i < this.visits4Show.length; i++) {
            shippingFromVisit = this.shippings.find(x => x.id === this.visits4Show[i].shipping_id)
            if (this.tour.shippings[i] && this.tour.shippings[i].current_tracking !== null && this.tour.courier_id !== null && alreadyTracking === false) {
              alreadyTracking = true
              icon = this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === this.tour.courier.courier_available.vehicle_type_id).name.toLowerCase() + '-gray.svg'

              infoWindowHtml = this.$gettextInterpolate(this.$gettext('<p>User: <strong>%{data}</strong></p>'), {data: this.tour.courier.name})
              infoWindowHtml += this.$gettextInterpolate(this.$gettext('<p>Email: <a href="mailto:%{data}"><strong>%{data}</strong></a></p>'), {data: this.tour.courier.email})
              infoWindowHtml += this.$gettextInterpolate(this.$gettext('<p>Phone: <a href="tel:%{data}"><strong>%{data}</strong></a></p>'), {data: this.tour.courier.phone})

              markers.push({
                id: 1000,
                lat: parseFloat(this.tour.shippings[i].current_tracking.lat),
                lng: parseFloat(this.tour.shippings[i].current_tracking.lng),
                icon: icon,
                x: 25,
                y: 25,
                infoText: {id: this.tour.courier.id, type: 'courier', html: infoWindowHtml, user: this.tour.courier},
                popup: true
              })
            }
            switch (this.visits4Show[i].visited) {
              case 0:
                // icon = 'https://avatars.discourse.org/v2/letter/' + (i + 1) + '/999999/24.png'
                icon = {
                  type: 'map-marker',
                  size: [27, 36],
                  anchor: [13.5, 36],
                  color: '#999',
                  text: (this.visits4Show[i].type === 'pickup' ? '↑' : '↓') + (i + 1)
                }
                break
              case 1:
                icon = {
                  type: 'map-marker',
                  size: [27, 36],
                  anchor: [13.5, 36],
                  color: '#28a745',
                  text: (this.visits4Show[i].type === 'pickup' ? '↑' : '↓') + (i + 1)
                }
                break
            }

            if (this.visits4Show[i].lat !== null) {
              lat = this.visits4Show[i].lat
              lng = this.visits4Show[i].lng
            }
            infoWindowHtml = this.$gettextInterpolate(this.$gettext('<p>Type: <strong>%{data}</strong></p>'), {data: this.visits4Show[i].type + (this.visits4Show[i].shipping_ids.length > 1 ? ' multiple' : '')})
            infoWindowHtml += this.$gettextInterpolate(this.$gettext('<p>Address: <strong>%{data}</strong></p>'), {data: this.visits4Show[i].address_name})
            infoWindowHtml += this.$gettextInterpolate(this.$gettext('<p>Address Extra: <strong>%{data}</strong></p>'), {data: this.visits4Show[i].address_extra || ''})
            infoWindowHtml += this.$gettextInterpolate(this.$gettext('<p>Visited: <strong>%{data}</strong></p>'), {data: (this.visits4Show[i].visited === 1 ? this.$gettext('YES') : this.$gettext('NO'))})

            markers.push({
              id: 100000 + i,
              lat: lat,
              lng: lng,
              icon: icon,
              x: 11,
              y: 34,
              infoText: {
                id: this.visits4Show[i].shipping_id,
                type: 'visit',
                multi: this.visits4Show[i].shipping_ids.length > 1,
                visited: shippingFromVisit.status === 'PICKEDUP' || shippingFromVisit.status === 'INTRANSIT' || shippingFromVisit.status === 'DELIVERED' || shippingFromVisit.status === 'CANCELED' || shippingFromVisit.status === 'RETURNED',
                html: infoWindowHtml,
                ids: this.visits4Show[i].shipping_ids
              },
              popup: true
            })
          }
          this.markers = markers
          console.log(markers)
        })
    },
    reloadTour() {
      this.$nextTick(() => {
        this.getTour()
      })
    },
    fetchFreeSlots() {
      if (this.warehouse) {
        this.$http.get(this.$appConfig.api.baseUrl + 'warehouses/' + this.warehouse.id + '/free-slots')
          .then(response => {
            this.freeSlots = response.body.data
          })
      }
    },
    assignSlot() {
      this.$http.post(this.$appConfig.api.baseUrl + 'warehouses/' + this.warehouse.id + '/tours/' + this.tour.id + '/slots/' + this.selectedSlot + '/assign')
        .then(response => {
          console.log(response.body)
          this.$refs.modalAssignSlot.hide()
          this.reloadTour()
        })
    },
    releaseSlot() {
      if (
        confirm(this.$gettext('Are you sure you want to FULL RELEASE the current Slot?'))
      ) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'warehouses-slots/' + this.tour.warehouse_slot.id + '/release')
          .then(response => {
            this.reloadTour()
          })
      }
    },
    onSearchCourierSelected(item) {
      this.newCourier = item
      this.$refs.courierAc.searchQuery = ''
    },
    assignCourier() {
      if (
        confirm(this.$gettext('Are you sure you want to ASSIGN the current tour to the selected courier?'))
      ) {
        this.$http.patch(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id, {courier_id: this.newCourier.id})
          .then(response => {
            this.reloadTour()
            this.$refs.modalAssignNormal.hide()
          })
      }
    },
    async unassignTour() {
      if (
        confirm(this.$gettext('Are you sure you want to UNASSIGN the current courier of the tour?'))
      ) {
        if (this.tour.fixed_amount_price_schedules[0]) {
          await this.deleteFixedPrice()
        }
        this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/reject')
          .then(response => {
            this.notOptimized = false
            this.reloadTour()
          })
      }
    },
    reassignCourier() {
      if (
        confirm(this.$gettext('Are you sure you want to REASSIGN the current tour to the selected courier?'))
      ) {
        this.working = true
        const redirectShippingId = this.tour.shippings.find(x => ['DELIVERED', 'CANCELED', 'RETURNED', 'TOBERETURNED'].indexOf(x.status) === -1).id
        this.$http.patch(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/reassign', {courier_id: this.newCourier.id})
          .then(async response => {
            this.$refs.modalAssignNormal.hide()
            await this.checkReassignJob(response.body.key, redirectShippingId)
          })
          .finally(() => {
            this.working = false
          })
      }
    },
    async checkReassignJob(id, redirectShippingId) {
      return await this.$http.get(this.$appConfig.api.baseUrl + 'tours/jobs/' + id + '/status')
        .then(response => {
          if (response.body.status === 'finished') {
            return this.$http.get(this.$appConfig.api.baseUrl + 'shippings/' + redirectShippingId)
              .then(r => {
                this.reloadTour()
                window.open('/edit/tours/' + r.body.tour_id)
              })
              .finally(() => {
                this.working = false
              })
          } else {
            setTimeout(async () => {
              return await this.checkReassignJob(id, redirectShippingId)
            }, 1000)
          }
        })
    },
    async deleteFixedPrice() {
      return this.$http.delete(this.$appConfig.api.baseUrl + 'fixed-amount-price-schedules/' + this.tour.fixed_amount_price_schedules[0].id)
    },
    optimizeTour() {
      if (
        confirm(this.$gettext('Are you sure you want to RESEND the current tour to Routifier?'))
      ) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/generate-route', this.start_location)
          .then(response => {
            this.notOptimized = false
            this.reloadTour()
          })
      }
    },
    setTourAsReady() {
      if (
        confirm(this.$gettext('Are you sure you want to reset this tour as READY?'))
      ) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/ready')
          .then(response => {
            this.notOptimized = false
            this.reloadTour()
          })
      }
    },
    cancelTour() {
      if (
        confirm(this.$gettext('Are you sure you want to CANCEL the current tour?'))
      ) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/cancel')
          .then(response => {
            this.notOptimized = false
            this.reloadTour()
          })
      }
    },
    addShippingToTour() {
      const newShippings = this.newShippings.split('\n').filter(Boolean)
      if (
        confirm(this.$gettextInterpolate(this.$gettext('Are you sure you want to ADD this/these %{qty} shipping/s to the current Tour?'), {qty: newShippings.length}))
      ) {
        this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/shippings', newShippings)
          .then(response => {
            this.notOptimized = false
            this.newShippings = null
            this.reloadTour()
          })
      }
    },
    deleteFromTour() {
      if (this.toDelete && this.deleteType) {
        if (
          confirm(this.$gettext('Are you sure you want to DELETE this shipping from the current Tour?'))
        ) {
          let params
          if (this.deleteType === 'A') {
            params = {
              shipping_id: this.toDelete.id,
              user_id: this.$store.state.auth.uid,
              status: 'READY'
            }
          } else if (this.deleteType === 'B' && this.toDelete.status !== 'CONFIRMED') {
            params = {
              shipping_id: this.toDelete.id,
              user_id: this.$store.state.auth.uid,
              status: 'WAITING'
            }
          } else if (this.deleteType === 'B' && this.toDelete.status === 'CONFIRMED') {
            params = {
              shipping_id: this.toDelete.id,
              user_id: this.$store.state.auth.uid,
              status: 'READY'
            }
            this.$http.post(this.$appConfig.api.baseUrl + 'status/', params)
              .then(response => {
                params = {
                  shipping_id: this.toDelete.id,
                  user_id: this.$store.state.auth.uid,
                  status: 'WAITING'
                }
                this.$http.post(this.$appConfig.api.baseUrl + 'status/', params)
                  .then(response => {
                    this.$http.delete(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/shippings', {body: [this.toDelete.id]})
                      .then(response => {
                        this.toDelete = {}
                        this.deleteType = null
                        this.notOptimized = false
                        this.reloadTour()
                        this.$refs.modalDeleteFromTour.hide()
                      })
                  })
              })
            return true
          }

          this.$http.post(this.$appConfig.api.baseUrl + 'status/', params)
            .then(response => {
              this.$http.delete(this.$appConfig.api.baseUrl + 'tours/' + this.$route.params.id + '/shippings', {body: [this.toDelete.id]})
                .then(response => {
                  this.toDelete = {}
                  this.deleteType = null
                  this.notOptimized = false
                  this.reloadTour()
                  this.$refs.modalDeleteFromTour.hide()
                })
            })
        }
      }
    },
    fixedPriceSuccess() {
      setTimeout(function () {
        this.getTour()
        this.$refs.modalFixedPrice.hide()
      }.bind(this), 1000)
    },
    fixedPriceCancel() {
      this.$refs.modalFixedPrice.hide()
    },
    recalculateBudget() {
      if (
        confirm(this.$gettext('Are you sure you want to RECALCULATE currently active Tour shippings\' Budgets?'))
      ) {
        this.$http.put(this.$appConfig.api.baseUrl + 'tours/' + this.tour.id + '/budgets/recalculate')
          .then(response => {
            this.getTour()
          })
      }
    }
  }
}
</script>
